/**
 * Docs area of the sidebar (shell container): Favorites, Workspace and Private page trees with expand/collapse,
 * the "+" template menu (Page, Database, Projects, Meeting notes; hidden for restricted members, who only add
 * subpages to pages shared with them), row actions (new subpage, rename, favourite,
 * move between sections, open in a new tab / split, trash with undo) and ⌘-click / middle-click to open in a new tab.
 */
import type { PageNode } from "@bundle/contracts";
import { Shell, type NewPage, type OpenTarget, type PageTreeRow, type PageTreeSectionId } from "@bundle/core";
import type { IconName } from "@bundle/tokens";
import { DocsSectionHeader, Icon, ListRow, SidebarPageRow, Toast, usePageTree, useT, useTemplatesViewModel, useTr, useWorkspace, type MenuItem } from "@bundle/ui";
import { useState, type ComponentType, type ReactNode } from "react";
import { featureFlags } from "../platform/features.ts";

const SECTION_ICONS: Record<PageTreeSectionId, IconName> = { favorites: "star", workspace: "globe", private: "lock", shared: "users" };
const COLLAPSED_KEY = "bundle.sidebar.docs.collapsed";

/** ⌘/Ctrl-click and middle-click open `url` in a new tab (the row's own click opens it in place). */
export function ModClick({ url, open, children }: { url: string; open: (url: string, target?: OpenTarget) => void; children: ReactNode }) {
  return (
    <div
      className="sidebar-link"
      onClickCapture={(e) => {
        if (!(e.metaKey || e.ctrlKey)) return;
        e.preventDefault();
        e.stopPropagation();
        open(url, "newTab");
      }}
      onAuxClick={(e) => {
        if (e.button !== 1) return;
        e.preventDefault();
        open(url, "newTab");
      }}
    >
      {children}
    </div>
  );
}

/**
 * Sidebar folders (featureFlags.channels): docs items I put in a folder are listed there instead of here, rows offer
 * "Move to folder…" and drag into folders (`Draggable` wraps each row).
 */
type FolderHooks = { hidden: string[]; onMoveToFolder: (pageId: string) => void; Draggable: ComponentType<{ pageId: string; children: ReactNode }> };

type Props = { activeUrl: string; open: (url: string, target?: OpenTarget) => void; canSplit: boolean; folders?: FolderHooks };

function readList(raw: string | undefined): string[] {
  try {
    const v: unknown = raw === undefined ? [] : JSON.parse(raw);
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function SidebarDocs({ activeUrl, open, canSplit, folders }: Props) {
  const t = useT();
  const tr = useTr();
  const ws = useWorkspace();
  const tree = usePageTree();
  const gallery = useTemplatesViewModel();
  const storage = ws.vm.deps.storage;
  const [collapsed, setCollapsed] = useState<string[]>(() => readList(storage.get(COLLAPSED_KEY)));
  const [renaming, setRenaming] = useState<string | undefined>(undefined);
  const [trashed, setTrashed] = useState<PageNode | undefined>(undefined);
  const active = Shell.parseUrl(activeUrl);
  const activeId = active?.type === "page" ? active.pageId : active?.type === "db" ? active.dbId : undefined;

  const urlOf = (id: string, kind: PageNode["kind"]) =>
    kind === "database" ? Shell.formatUrl({ type: "db", ws: ws.slug, dbId: id }) : Shell.formatUrl({ type: "page", ws: ws.slug, pageId: id });

  const create = async (input: NewPage) => {
    const page = await tree.vm.create(input);
    if (page !== undefined) open(urlOf(page.id, page.kind));
  };

  const toggleSection = (id: string) => {
    const next = collapsed.includes(id) ? collapsed.filter((x) => x !== id) : [...collapsed, id];
    storage.set(COLLAPSED_KEY, JSON.stringify(next));
    setCollapsed(next);
  };

  const templates = (visibility: "workspace" | "private"): MenuItem[] => [
    { id: "new-page", label: t("docs.template.page"), icon: "page", onSelect: () => void create({ visibility }) },
    { id: "new-database", label: t("docs.template.database"), icon: "database", onSelect: () => void create({ kind: "database", visibility }) },
    { id: "new-projects", label: t("docs.template.projects"), icon: "board", onSelect: () => void create({ template: "projects", visibility }) },
    { id: "new-meeting", label: t("docs.template.meetingNotes"), icon: "notes", onSelect: () => void create({ template: "meeting_notes", visibility }) },
    // The template gallery (featureFlags.templates; the context is only provided when it is on).
    ...(gallery === null ? [] : [{ id: "new-from-template", label: t("templates.open"), icon: "sparkles" as const, onSelect: () => gallery.openGallery({ visibility }) }]),
  ];

  const rowMenu = (row: PageTreeRow): MenuItem[] => {
    const page = tree.vm.page(row.id);
    const url = urlOf(row.id, row.kind);
    const items: MenuItem[] = [];
    if (row.editable && row.kind === "page") items.push({ id: "subpage", label: t("docs.menu.newSubpage"), icon: "pagePlus", onSelect: () => void create({ parentId: row.id }) });
    if (row.editable) items.push({ id: "rename", label: t("docs.menu.rename"), icon: "edit", onSelect: () => setRenaming(row.key) });
    items.push(
      row.favorite
        ? { id: "unfavorite", label: t("docs.menu.removeFavorite"), icon: "starOff", onSelect: () => tree.vm.setFavorite(row.id, false) }
        : { id: "favorite", label: t("docs.menu.addFavorite"), icon: "star", onSelect: () => tree.vm.setFavorite(row.id, true) },
    );
    if (row.editable && page !== undefined && page.parentId === undefined && row.section !== "favorites") {
      items.push(
        page.visibility === "workspace"
          ? { id: "to-private", label: t("docs.menu.moveToPrivate"), icon: "lock", onSelect: () => tree.vm.setVisibility(row.id, "private") }
          : { id: "to-workspace", label: t("docs.menu.moveToWorkspace"), icon: "globe", onSelect: () => tree.vm.setVisibility(row.id, "workspace") },
      );
    }
    if (folders !== undefined) items.push({ id: "move-to-folder", label: t("sidebar.row.move"), icon: "folder", onSelect: () => folders.onMoveToFolder(row.id) });
    if (gallery !== null) {
      // Duplicate next to it (edit access where it goes), Save as template (any page I can see; not guests or agents).
      if (row.editable && (page?.parentId !== undefined || tree.state.canCreatePages)) {
        items.push({
          id: "duplicate",
          label: t("templates.duplicate"),
          icon: "copy",
          onSelect: () =>
            void gallery.duplicate(row.id).then((p) => {
              if (p !== undefined) open(urlOf(p.id, p.kind));
            }),
        });
      }
      if (tree.state.canCreatePages) items.push({ id: "save-template", label: t("templates.saveAs"), icon: "bookmark", onSelect: () => gallery.openSave(row.id) });
    }
    items.push({ id: "new-tab", label: t("shell.openInNewTab"), icon: "tabs", onSelect: () => open(url, "newTab") });
    if (canSplit) items.push({ id: "split", label: t("shell.newTabInSplit"), icon: "splitH", onSelect: () => open(url, "split") });
    if (row.editable) {
      items.push({
        id: "trash",
        label: t("docs.menu.trash"),
        icon: "trash",
        destructive: true,
        onSelect: () => {
          if (page !== undefined) setTrashed(page);
          tree.vm.trash(row.id);
        },
      });
    }
    return items;
  };

  return (
    <>
      {tree.state.sections.map((section) => {
        const isCollapsed = collapsed.includes(section.id);
        return (
          <div className="sidebar-group" key={section.id} data-testid={`docs-${section.id}`}>
            <DocsSectionHeader
              title={tr(section.title)}
              icon={SECTION_ICONS[section.id]}
              collapsed={isCollapsed}
              onToggle={() => toggleSection(section.id)}
              addMenu={section.id === "favorites" || section.id === "shared" || !tree.state.canCreatePages ? undefined : templates(section.id)}
              addLabel={t("docs.newPage")}
              testID={`section-docs-${section.id}`}
            />
            {isCollapsed || (section.rows.length === 0 && !tree.state.canCreatePages) ? null : section.rows.length === 0 ? (
              <ListRow
                title={t("docs.addPage")}
                leading={<Icon name="plus" size="icon.sm" color="text.tertiary" />}
                muted
                onPress={() => void create({ visibility: section.id === "private" ? "private" : "workspace" })}
                testID={`docs-${section.id}-empty`}
              />
            ) : (
              section.rows.filter((row) => folders === undefined || section.id === "favorites" || !folders.hidden.includes(row.id)).map((row) => {
                const url = urlOf(row.id, row.kind);
                const content = (
                  <ModClick key={row.key} url={url} open={open}>
                    <SidebarPageRow
                      row={row}
                      selected={row.id === activeId}
                      onPress={() => open(url)}
                      onToggle={() => tree.vm.toggle(row.key)}
                      menu={rowMenu(row)}
                      onAddSubpage={row.editable && row.kind === "page" ? () => void create({ parentId: row.id }) : undefined}
                      renaming={renaming === row.key}
                      onRename={(title) => {
                        setRenaming(undefined);
                        tree.vm.rename(row.id, title);
                      }}
                      onCancelRename={() => setRenaming(undefined)}
                      showBadge={featureFlags.sharing}
                    />
                  </ModClick>
                );
                return folders === undefined ? content : <folders.Draggable key={row.key} pageId={row.id}>{content}</folders.Draggable>;
              })
            )}
          </div>
        );
      })}
      <Toast
        open={trashed !== undefined}
        onClose={() => setTrashed(undefined)}
        message={t("docs.trashed")}
        action={{
          label: t("docs.undo"),
          onPress: () => {
            if (trashed !== undefined) tree.vm.restore(trashed.id);
            setTrashed(undefined);
          },
          testID: "docs-trash-undo",
        }}
        closeLabel={t("common.close")}
        durationMs={6000}
        testID="docs-trash-toast"
      />
      <Toast open={tree.state.error !== undefined} onClose={() => tree.vm.dismissError()} message={t("docs.createFailed")} closeLabel={t("common.close")} durationMs={6000} testID="docs-error-toast" />
    </>
  );
}
